/**
 * Motor de audio procedural basado en Web Audio API.
 *
 * Genera ambientes sonoros y efectos de sonido sin archivos externos.
 * Los perfiles de ambiente se mapean desde timeOfDay + ambiance del JSON.
 * Todos los sonidos respetan reducedMotion (silencio si está activo).
 */

export type AmbientProfile = 'dusk-dust' | 'night' | 'dawn-mist' | 'day' | 'rain' | 'fireflies' | 'embers' | 'silent';

export class ProceduralAudio {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private currentAmbient: AmbientProfile | null = null;
  private ambientNodes: AudioNode[] = [];
  private ambientGains: GainNode[] = [];
  private reducedMotion = false;

  private musicGain: GainNode | null = null;
  private musicEnabled = false;
  private musicTimer: number | null = null;
  private musicBar = 0;
  private destroyed = false;

  private static readonly MUSIC_PROGRESSION: number[][] = [
    [220.00, 261.63, 329.63], // Am
    [174.61, 220.00, 261.63], // F
    [261.63, 329.63, 392.00], // C
    [196.00, 246.94, 293.66]  // G
  ];
  private static readonly MUSIC_BAR_MS = 3200;

  constructor() {}

  setReducedMotion(value: boolean): void {
    this.reducedMotion = value;
    if (value) {
      this.stopAmbient();
      this.stopMusic();
    }
  }

  /* --------------------------- Música de fondo --------------------------- */

  setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    if (enabled) this.startMusic();
    else this.stopMusic();
  }

  get isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  /** Inicia el bucle musical (requiere gesto previo del usuario). */
  startMusic(): void {
    if (!this.musicEnabled || this.reducedMotion || this.musicTimer !== null) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.masterGain) return;
    if (!this.musicGain) {
      this.musicGain = ctx.createGain();
      this.musicGain.gain.value = 0;
      this.musicGain.connect(this.masterGain);
    }
    this.musicGain.gain.cancelScheduledValues(ctx.currentTime);
    this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, ctx.currentTime);
    this.musicGain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 1.2);
    this.scheduleMusicBar();
  }

  stopMusic(): void {
    if (this.musicTimer !== null) {
      clearTimeout(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicGain && this.ctx) {
      const now = this.ctx.currentTime;
      this.musicGain.gain.cancelScheduledValues(now);
      this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, now);
      this.musicGain.gain.linearRampToValueAtTime(0, now + 0.6);
    }
  }

  private scheduleMusicBar(): void {
    if (!this.musicEnabled || this.destroyed) return;
    this.playMusicBar(this.musicBar % ProceduralAudio.MUSIC_PROGRESSION.length);
    this.musicBar++;
    this.musicTimer = window.setTimeout(() => {
      this.musicTimer = null;
      this.scheduleMusicBar();
    }, ProceduralAudio.MUSIC_BAR_MS);
  }

  private playMusicBar(chordIdx: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicGain) return;
    const chord = ProceduralAudio.MUSIC_PROGRESSION[chordIdx];
    const now = ctx.currentTime;
    const barDur = ProceduralAudio.MUSIC_BAR_MS / 1000;

    // Pad: acorde sostenido con ataque y caída suaves
    for (const freq of chord) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq / 2;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.05, now + barDur * 0.3);
      gain.gain.linearRampToValueAtTime(0, now + barDur);
      osc.connect(gain).connect(this.musicGain);
      osc.start(now);
      osc.stop(now + barDur + 0.1);
    }

    // Arpegio: notas pentatónicas espaciadas, registro medio-alto
    const arpCount = 8;
    for (let i = 0; i < arpCount; i++) {
      const t = now + (i * barDur) / arpCount + Math.random() * 0.04;
      const freq = chord[(i * 2 + chordIdx) % chord.length] * (i % 3 === 2 ? 4 : 2);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.05, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      osc.connect(gain).connect(this.musicGain);
      osc.start(t);
      osc.stop(t + 0.55);
    }
  }

  /** Inicializa el contexto de audio (requiere interacción del usuario). */
  private ensureContext(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.value = 0.3; // Volumen maestro bajo
        this.masterGain.connect(this.ctx.destination);
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  /** Activa/desactiva el audio (silencia sin destruir nodos). */
  setEnabled(enabled: boolean): void {
    if (this.masterGain) {
      this.masterGain.gain.value = enabled ? 0.3 : 0;
    }
  }

  /** Reproduce un perfil de ambiente con crossfade. */
  playAmbient(profile: AmbientProfile): void {
    if (this.reducedMotion) return;
    if (profile === this.currentAmbient) return;
    this.currentAmbient = profile;

    const ctx = this.ensureContext();
    if (!ctx || !this.masterGain) return;

    // Fade out anterior
    this.fadeOutAmbient(1500);

    // Fade in nuevo
    setTimeout(() => {
      this.startAmbientProfile(profile, ctx);
    }, 200);
  }

  /** Detiene el ambiente actual con fade out. */
  stopAmbient(): void {
    this.fadeOutAmbient(1500);
    this.currentAmbient = null;
  }

  private fadeOutAmbient(durationMs: number): void {
    for (const gain of this.ambientGains) {
      const ctx = this.ctx;
      if (!ctx) continue;
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + durationMs / 1000);
    }
    // Limpiar nodos después del fade
    setTimeout(() => {
      for (const node of this.ambientNodes) {
        try { node.disconnect(); } catch {}
        if ('stop' in node) {
          try { (node as OscillatorNode).stop(); } catch {}
        }
      }
      this.ambientNodes = [];
      this.ambientGains = [];
    }, durationMs + 100);
  }

  private startAmbientProfile(profile: AmbientProfile, ctx: AudioContext): void {
    switch (profile) {
      case 'dusk-dust':
        this.playDuskDust(ctx);
        break;
      case 'night':
        this.playNight(ctx);
        break;
      case 'dawn-mist':
        this.playDawnMist(ctx);
        break;
      case 'day':
        this.playDay(ctx);
        break;
      case 'rain':
        this.playRain(ctx);
        break;
      case 'fireflies':
        this.playFireflies(ctx);
        break;
      case 'embers':
        this.playDuskDust(ctx);
        break;
      case 'silent':
        // Sin sonido
        break;
    }
  }

  /** Drone cálido + pájaros lejanos. */
  private playDuskDust(ctx: AudioContext): void {
    // Drone: sine 110Hz + 165Hz
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 110;
    const gain1 = ctx.createGain();
    gain1.gain.value = 0.15;
    osc1.connect(gain1).connect(this.masterGain!);
    osc1.start();
    this.ambientNodes.push(osc1);
    this.ambientGains.push(gain1);

    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 165;
    const gain2 = ctx.createGain();
    gain2.gain.value = 0.1;
    osc2.connect(gain2).connect(this.masterGain!);
    osc2.start();
    this.ambientNodes.push(osc2);
    this.ambientGains.push(gain2);

    // Pájaros: noise bursts filtrados BP 2-4kHz cada 3-8s
    this.scheduleBirds(ctx);
  }

  private scheduleBirds(ctx: AudioContext): void {
    if (this.currentAmbient !== 'dusk-dust') return;
    const delay = 3000 + Math.random() * 5000;
    setTimeout(() => {
      if (this.currentAmbient !== 'dusk-dust') return;
      this.playBirdChirp(ctx);
      this.scheduleBirds(ctx);
    }, delay);
  }

  private playBirdChirp(ctx: AudioContext): void {
    const bufferSize = ctx.sampleRate * 0.15; // 150ms
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3));
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 3000;
    filter.Q.value = 2;

    const gain = ctx.createGain();
    gain.gain.value = 0.08;

    source.connect(filter).connect(gain).connect(this.masterGain!);
    source.start();
    this.ambientNodes.push(source);
  }

  /** Drone grave + grillos. */
  private playNight(ctx: AudioContext): void {
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 65;
    const gain1 = ctx.createGain();
    gain1.gain.value = 0.18;
    osc1.connect(gain1).connect(this.masterGain!);
    osc1.start();
    this.ambientNodes.push(osc1);
    this.ambientGains.push(gain1);

    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.value = 98;
    const gain2 = ctx.createGain();
    gain2.gain.value = 0.1;
    osc2.connect(gain2).connect(this.masterGain!);
    osc2.start();
    this.ambientNodes.push(osc2);
    this.ambientGains.push(gain2);

    // Grillos: pulsos de noise 6-10kHz, 15-20/s
    this.scheduleCrickets(ctx);
  }

  private scheduleCrickets(ctx: AudioContext): void {
    if (this.currentAmbient !== 'night') return;
    const interval = 50 + Math.random() * 20; // 50-70ms
    setTimeout(() => {
      if (this.currentAmbient !== 'night') return;
      this.playCricket(ctx);
      this.scheduleCrickets(ctx);
    }, interval);
  }

  private playCricket(ctx: AudioContext): void {
    const bufferSize = ctx.sampleRate * 0.02; // 20ms
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.5));
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 6000;

    const gain = ctx.createGain();
    gain.gain.value = 0.05;

    source.connect(filter).connect(gain).connect(this.masterGain!);
    source.start();
    this.ambientNodes.push(source);
  }

  /** Drone suave + viento. */
  private playDawnMist(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 130;
    const gain = ctx.createGain();
    gain.gain.value = 0.12;
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    this.ambientNodes.push(osc);
    this.ambientGains.push(gain);

    // Viento: noise rosa filtrado LP 800Hz
    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800;

    const windGain = ctx.createGain();
    windGain.gain.value = 0.15;

    source.connect(filter).connect(windGain).connect(this.masterGain!);
    source.start();
    this.ambientNodes.push(source);
    this.ambientGains.push(windGain);
  }

  /** Drone muy sutil. */
  private playDay(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 220;
    const gain = ctx.createGain();
    gain.gain.value = 0.08;
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    this.ambientNodes.push(osc);
    this.ambientGains.push(gain);
  }

  /** Lluvia: noise blanco filtrado + gotas. */
  private playRain(ctx: AudioContext): void {
    const bufferSize = ctx.sampleRate * 3;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000;
    filter.Q.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.value = 0.2;

    source.connect(filter).connect(gain).connect(this.masterGain!);
    source.start();
    this.ambientNodes.push(source);
    this.ambientGains.push(gain);

    // Gotas: impulsos aleatorios 2-6kHz
    this.scheduleRaindrops(ctx);
  }

  private scheduleRaindrops(ctx: AudioContext): void {
    if (this.currentAmbient !== 'rain') return;
    const delay = 100 + Math.random() * 300;
    setTimeout(() => {
      if (this.currentAmbient !== 'rain') return;
      this.playRaindrop(ctx);
      this.scheduleRaindrops(ctx);
    }, delay);
  }

  private playRaindrop(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 2000 + Math.random() * 4000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
    this.ambientNodes.push(osc);
  }

  /** Drone nocturno + brillos. */
  private playFireflies(ctx: AudioContext): void {
    this.playNight(ctx); // Base nocturna
    // Brillos: sine 800Hz pulsos suaves aleatorios
    this.scheduleFireflyGlow(ctx);
  }

  private scheduleFireflyGlow(ctx: AudioContext): void {
    if (this.currentAmbient !== 'fireflies') return;
    const delay = 2000 + Math.random() * 4000;
    setTimeout(() => {
      if (this.currentAmbient !== 'fireflies') return;
      this.playFireflyGlow(ctx);
      this.scheduleFireflyGlow(ctx);
    }, delay);
  }

  private playFireflyGlow(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 800;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.5);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.5);
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    osc.stop(ctx.currentTime + 1.5);
    this.ambientNodes.push(osc);
  }

  /* --------------------------- Efectos de sonido (SFX) --------------------------- */

  /** Clic UI / hotspot. */
  playClick(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.reducedMotion) return;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.06);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    osc.stop(ctx.currentTime + 0.06);
  }

  /** Transición de escena (whoosh). */
  playWhoosh(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.reducedMotion) return;
    const bufferSize = ctx.sampleRate * 0.3;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.5));
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(200, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.3);

    const gain = ctx.createGain();
    gain.gain.value = 0.2;

    source.connect(filter).connect(gain).connect(this.masterGain!);
    source.start();
  }

  /** Evidencia recogida (chime). */
  playChime(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.reducedMotion) return;
    const freqs = [523, 659];
    for (const freq of freqs) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.connect(gain).connect(this.masterGain!);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    }
  }

  /** Objetivo completado (mini-fanfarria). */
  playFanfare(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.reducedMotion) return;
    const notes = [523, 659, 784];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const startTime = ctx.currentTime + i * 0.15;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.15, startTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);
      osc.connect(gain).connect(this.masterGain!);
      osc.start(startTime);
      osc.stop(startTime + 0.2);
    });
  }

  /** Diálogo abierto (soft pop). */
  playPop(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.reducedMotion) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.08);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
  }

  /** Error / bloqueo (buzz). */
  playBuzz(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.reducedMotion) return;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 150;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.connect(gain).connect(this.masterGain!);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  }

  /** Despacha un SFX por nombre (para secuencias y acciones del JSON). */
  playSfxByName(name: string): void {
    switch (name) {
      case 'click': this.playClick(); break;
      case 'whoosh': this.playWhoosh(); break;
      case 'chime': this.playChime(); break;
      case 'fanfare': this.playFanfare(); break;
      case 'pop': this.playPop(); break;
      case 'buzz': this.playBuzz(); break;
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.musicEnabled = false;
    this.stopMusic();
    this.stopAmbient();
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
      this.masterGain = null;
    }
  }
}
